import * as ort from "onnxruntime-web/wasm";

export interface RuntimeStats {
  stepMs: number;
  kvMs: number;
  decodeMs: number;
  cacheFrames: number;
  generatedFrames: number;
}

export interface ModelUrls {
  audioEncoder?: string;
  transformerKv: string;
  tokenDecoder: string;
  samplingTemperature?: number;
  topK?: number;
  repetitionPenalty?: number;
}

export class GestureOnnxRuntime {
  private audioSession: ort.InferenceSession | null = null;
  private kvSession: ort.InferenceSession | null = null;
  private decoderSession: ort.InferenceSession | null = null;
  private currentTokens: BigInt64Array<ArrayBufferLike> = new BigInt64Array(6);
  private generated: BigInt64Array<ArrayBufferLike>[] = [];
  private regionK!: Float32Array<ArrayBufferLike>;
  private regionV!: Float32Array<ArrayBufferLike>;
  private fusionK!: Float32Array<ArrayBufferLike>;
  private fusionV!: Float32Array<ArrayBufferLike>;
  private cacheFrames = 0;
  private stepCount = 0;
  private lastAudioDiagnostics: AudioDiagnostics | null = null;

  readonly batch = 1;
  readonly regions = 6;
  readonly vocabSize = 512;
  readonly bosTokenId = 512;
  readonly layers = 4;
  readonly heads = 4;
  readonly headDim = 64;
  readonly audioDim = 80;
  readonly textDim = 16;
  readonly audioSamples = 2048;
  readonly decoderFrames = 8;
  maxCacheFrames = 128;
  samplingTemperature = 0;
  topK: number | null = null;
  repetitionPenalty = 1;

  get loaded(): boolean {
    return this.kvSession !== null && this.decoderSession !== null;
  }

  async load(modelUrls: ModelUrls): Promise<void> {
    this.samplingTemperature = modelUrls.samplingTemperature ?? 0;
    this.topK = modelUrls.topK ?? null;
    this.repetitionPenalty = modelUrls.repetitionPenalty ?? 1;
    ort.env.wasm.wasmPaths = "/ort/";
    ort.env.wasm.numThreads = self.crossOriginIsolated
      ? Math.max(1, Math.min(4, navigator.hardwareConcurrency || 1))
      : 1;
    ort.env.wasm.simd = true;
    if (modelUrls.audioEncoder) {
      try {
        this.audioSession = await ort.InferenceSession.create(modelUrls.audioEncoder, {
          executionProviders: ["wasm"]
        });
      } catch (error) {
        console.warn("Audio encoder ONNX failed to load; falling back to browser FFT features", error);
        this.audioSession = null;
      }
    }
    this.kvSession = await ort.InferenceSession.create(modelUrls.transformerKv, {
      executionProviders: ["wasm"]
    });
    this.decoderSession = await ort.InferenceSession.create(modelUrls.tokenDecoder, {
      executionProviders: ["wasm"]
    });
    console.info("ONNX runtime loaded", {
      wasm: {
        crossOriginIsolated: self.crossOriginIsolated,
        numThreads: ort.env.wasm.numThreads,
        simd: ort.env.wasm.simd
      },
      models: {
        audioEncoder: this.audioSession
          ? {
              url: modelUrls.audioEncoder,
              inputs: this.audioSession.inputNames,
              outputs: this.audioSession.outputNames
            }
          : null,
        transformerKv: {
          url: modelUrls.transformerKv,
          inputs: this.kvSession.inputNames,
          outputs: this.kvSession.outputNames
        },
        tokenDecoder: {
          url: modelUrls.tokenDecoder,
          inputs: this.decoderSession.inputNames,
          outputs: this.decoderSession.outputNames
        }
      },
      shapes: {
        batch: this.batch,
        regions: this.regions,
        layers: this.layers,
        heads: this.heads,
        headDim: this.headDim,
        audioDim: this.audioDim,
        audioSamples: this.audioSamples,
        textDim: this.textDim,
        vocabSize: this.vocabSize,
        bosTokenId: this.bosTokenId,
        decoderFrames: this.decoderFrames,
        maxCacheFrames: this.maxCacheFrames
      },
      generation: {
        samplingTemperature: this.samplingTemperature,
        topK: this.topK,
        repetitionPenalty: this.repetitionPenalty
      }
    });
    this.reset();
  }

  reset(): void {
    this.currentTokens = new BigInt64Array(this.regions);
    this.currentTokens.fill(BigInt(this.bosTokenId));
    this.generated = [];
    this.cacheFrames = 0;
    this.regionK = new Float32Array(0);
    this.regionV = new Float32Array(0);
    this.fusionK = new Float32Array(0);
    this.fusionV = new Float32Array(0);
    this.stepCount = 0;
    this.lastAudioDiagnostics = null;
  }

  async encodeAudio(waveform: Float32Array<ArrayBufferLike>): Promise<Float32Array<ArrayBufferLike> | null> {
    if (!this.audioSession) {
      return null;
    }
    const started = performance.now();
    const output = await this.audioSession.run({
      waveform: new ort.Tensor("float32", waveform, [this.batch, waveform.length])
    });
    const feature = output.audio_features.data as Float32Array;
    this.lastAudioDiagnostics = {
      ms: performance.now() - started,
      waveform: summarizeFloat32(waveform),
      feature: summarizeFloat32(feature)
    };
    return feature;
  }

  async step(
    audioFeature: Float32Array<ArrayBufferLike>,
    textFeature: Float32Array<ArrayBufferLike> | null = null
  ): Promise<{ motion: Float32Array<ArrayBufferLike> | null; stats: RuntimeStats }> {
    if (!this.kvSession || !this.decoderSession) {
      throw new Error("ONNX sessions are not loaded");
    }
    const started = performance.now();
    const feeds: Record<string, ort.Tensor> = {
      current_tokens: new ort.Tensor("int64", this.currentTokens, [this.batch, this.regions]),
      current_audio_features: new ort.Tensor("float32", audioFeature, [this.batch, this.audioDim]),
      past_region_k: new ort.Tensor("float32", this.regionK, [
        this.layers,
        this.regions,
        this.batch,
        this.heads,
        this.cacheFrames,
        this.headDim
      ]),
      past_region_v: new ort.Tensor("float32", this.regionV, [
        this.layers,
        this.regions,
        this.batch,
        this.heads,
        this.cacheFrames,
        this.headDim
      ]),
      past_fusion_k: new ort.Tensor("float32", this.fusionK, [1, this.batch, this.heads, this.cacheFrames, this.headDim]),
      past_fusion_v: new ort.Tensor("float32", this.fusionV, [1, this.batch, this.heads, this.cacheFrames, this.headDim])
    };
    const usesTextFeatures = this.kvSession.inputNames.includes("current_text_features");
    if (usesTextFeatures) {
      feeds.current_text_features = new ort.Tensor(
        "float32",
        textFeature ?? new Float32Array(this.textDim),
        [this.batch, this.textDim]
      );
    }
    const kvStarted = performance.now();
    const output = await this.kvSession.run(feeds);
    const kvMs = performance.now() - kvStarted;
    const logits = output.logits.data as Float32Array;
    const previousTokens = this.currentTokens;
    const tokenResult = sampleRegions(logits, previousTokens, {
      regions: this.regions,
      vocab: this.vocabSize,
      temperature: this.samplingTemperature,
      topK: this.topK,
      repetitionPenalty: this.repetitionPenalty
    });
    this.currentTokens = tokenResult.tokens;
    this.generated.push(new BigInt64Array(this.currentTokens));
    this.regionK = output.new_region_k.data as Float32Array;
    this.regionV = output.new_region_v.data as Float32Array;
    this.fusionK = output.new_fusion_k.data as Float32Array;
    this.fusionV = output.new_fusion_v.data as Float32Array;
    this.cacheFrames += 1;
    this.trimCache();

    const decodeWindow = createDecoderWindow(this.generated, this.regions, this.decoderFrames);
    const decodeStarted = performance.now();
    const decoded = await this.decoderSession.run({
      tokens: new ort.Tensor("int64", decodeWindow.tokens, [this.batch, this.decoderFrames, this.regions])
    });
    const decodeMs = performance.now() - decodeStarted;
    const motion = decoded.motion.data as Float32Array;
    const lastFrameOffset = decodeWindow.frameIndex * 56 * 3;
    const frame = motion.slice(lastFrameOffset, lastFrameOffset + 56 * 3);
    this.stepCount += 1;
    if (this.stepCount % 30 === 0) {
      console.info("ONNX stream diagnostics", {
        step: this.stepCount,
        audio: this.lastAudioDiagnostics,
        text: usesTextFeatures && textFeature ? summarizeFloat32(textFeature) : null,
        tokens: Array.from(this.currentTokens, (value) => Number(value)),
        tokenChanges: countTokenChanges(previousTokens, this.currentTokens),
        logits: tokenResult.diagnostics,
        motion: summarizeFloat32(frame)
      });
    }
    return {
      motion: frame,
      stats: {
        stepMs: performance.now() - started,
        kvMs,
        decodeMs,
        cacheFrames: this.cacheFrames,
        generatedFrames: this.generated.length
      }
    };
  }

  private trimCache(): void {
    if (this.cacheFrames <= this.maxCacheFrames) {
      return;
    }
    const keep = this.maxCacheFrames;
    this.regionK = trimRegionCache(this.regionK, this.layers, this.regions, this.batch, this.heads, this.cacheFrames, this.headDim, keep);
    this.regionV = trimRegionCache(this.regionV, this.layers, this.regions, this.batch, this.heads, this.cacheFrames, this.headDim, keep);
    this.fusionK = trimFusionCache(this.fusionK, this.batch, this.heads, this.cacheFrames, this.headDim, keep);
    this.fusionV = trimFusionCache(this.fusionV, this.batch, this.heads, this.cacheFrames, this.headDim, keep);
    this.cacheFrames = keep;
  }
}

interface AudioDiagnostics {
  ms: number;
  waveform: ReturnType<typeof summarizeFloat32>;
  feature: ReturnType<typeof summarizeFloat32>;
}

function countTokenChanges(previous: BigInt64Array<ArrayBufferLike>, current: BigInt64Array<ArrayBufferLike>): number {
  let changes = 0;
  for (let i = 0; i < current.length; i += 1) {
    if (previous[i] !== current[i]) {
      changes += 1;
    }
  }
  return changes;
}

function summarizeFloat32(values: Float32Array<ArrayBufferLike>): { min: number; max: number; meanAbs: number } {
  let min = Infinity;
  let max = -Infinity;
  let sumAbs = 0;
  for (const value of values) {
    min = Math.min(min, value);
    max = Math.max(max, value);
    sumAbs += Math.abs(value);
  }
  return {
    min,
    max,
    meanAbs: values.length > 0 ? sumAbs / values.length : 0
  };
}

interface SamplingConfig {
  regions: number;
  vocab: number;
  temperature: number;
  topK: number | null;
  repetitionPenalty: number;
}

interface TokenDiagnostics {
  mode: "argmax" | "sample";
  topK: number | null;
  temperature: number;
  repetitionPenalty: number;
  regions: Array<{
    region: number;
    selected: number;
    greedy: number;
    topLogit: number;
    secondLogit: number;
    margin: number;
  }>;
}

function sampleRegions(
  logits: Float32Array<ArrayBufferLike>,
  previousTokens: BigInt64Array<ArrayBufferLike>,
  config: SamplingConfig
): { tokens: BigInt64Array<ArrayBufferLike>; diagnostics: TokenDiagnostics } {
  if (config.temperature <= 0) {
    const tokens = argmaxRegions(logits, config.regions, config.vocab);
    return {
      tokens,
      diagnostics: tokenDiagnostics(logits, tokens, config, "argmax")
    };
  }
  const tokens = new BigInt64Array(config.regions);
  for (let region = 0; region < config.regions; region += 1) {
    tokens[region] = BigInt(sampleRegion(logits, previousTokens, region, config));
  }
  return {
    tokens,
    diagnostics: tokenDiagnostics(logits, tokens, config, "sample")
  };
}

function sampleRegion(
  logits: Float32Array<ArrayBufferLike>,
  previousTokens: BigInt64Array<ArrayBufferLike>,
  region: number,
  config: SamplingConfig
): number {
  const offset = region * config.vocab;
  const candidates = topCandidates(logits, offset, config.vocab, config.topK ?? config.vocab);
  const previous = Number(previousTokens[region]);
  const temperature = Math.max(1e-4, config.temperature);
  let maxValue = -Infinity;
  const scaled = candidates.map((candidate) => {
    const penalty = candidate.index === previous && config.repetitionPenalty > 1 ? config.repetitionPenalty : 1;
    const value = logits[offset + candidate.index] / penalty / temperature;
    maxValue = Math.max(maxValue, value);
    return { index: candidate.index, value };
  });
  let total = 0;
  const weights = scaled.map((candidate) => {
    const weight = Math.exp(candidate.value - maxValue);
    total += weight;
    return { index: candidate.index, weight };
  });
  let cursor = Math.random() * total;
  for (const candidate of weights) {
    cursor -= candidate.weight;
    if (cursor <= 0) {
      return candidate.index;
    }
  }
  return weights[weights.length - 1]?.index ?? 0;
}

function argmaxRegions(logits: Float32Array<ArrayBufferLike>, regions: number, vocab: number): BigInt64Array<ArrayBufferLike> {
  const tokens = new BigInt64Array(regions);
  for (let region = 0; region < regions; region += 1) {
    let best = 0;
    let bestValue = -Infinity;
    const offset = region * vocab;
    for (let i = 0; i < vocab; i += 1) {
      const value = logits[offset + i];
      if (value > bestValue) {
        bestValue = value;
        best = i;
      }
    }
    tokens[region] = BigInt(best);
  }
  return tokens;
}

function tokenDiagnostics(
  logits: Float32Array<ArrayBufferLike>,
  tokens: BigInt64Array<ArrayBufferLike>,
  config: SamplingConfig,
  mode: "argmax" | "sample"
): TokenDiagnostics {
  return {
    mode,
    topK: config.topK,
    temperature: config.temperature,
    repetitionPenalty: config.repetitionPenalty,
    regions: Array.from({ length: config.regions }, (_, region) => {
      const offset = region * config.vocab;
      const [top, second] = topCandidates(logits, offset, config.vocab, 2);
      return {
        region,
        selected: Number(tokens[region]),
        greedy: top?.index ?? 0,
        topLogit: top?.value ?? 0,
        secondLogit: second?.value ?? 0,
        margin: top && second ? top.value - second.value : 0
      };
    })
  };
}

function topCandidates(
  logits: Float32Array<ArrayBufferLike>,
  offset: number,
  vocab: number,
  count: number
): Array<{ index: number; value: number }> {
  const candidates: Array<{ index: number; value: number }> = [];
  const limit = Math.max(1, Math.min(vocab, count));
  for (let index = 0; index < vocab; index += 1) {
    const value = logits[offset + index];
    const insertAt = candidates.findIndex((candidate) => value > candidate.value);
    if (insertAt === -1) {
      if (candidates.length < limit) {
        candidates.push({ index, value });
      }
    } else {
      candidates.splice(insertAt, 0, { index, value });
      if (candidates.length > limit) {
        candidates.pop();
      }
    }
  }
  return candidates;
}

function createDecoderWindow(
  frames: BigInt64Array<ArrayBufferLike>[],
  regions: number,
  decoderFrames: number
): { tokens: BigInt64Array<ArrayBuffer>; frameIndex: number } {
  const out = new BigInt64Array(decoderFrames * regions);
  const start = Math.max(0, frames.length - decoderFrames);
  const windowFrames = frames.slice(start);
  windowFrames.forEach((frame, frameIndex) => out.set(frame, frameIndex * regions));
  return {
    tokens: out,
    frameIndex: Math.max(0, windowFrames.length - 1)
  };
}

function trimRegionCache(
  data: Float32Array<ArrayBufferLike>,
  layers: number,
  regions: number,
  batch: number,
  heads: number,
  frames: number,
  headDim: number,
  keep: number
): Float32Array<ArrayBuffer> {
  const out = new Float32Array(layers * regions * batch * heads * keep * headDim);
  let dst = 0;
  for (let l = 0; l < layers; l += 1) {
    for (let r = 0; r < regions; r += 1) {
      for (let b = 0; b < batch; b += 1) {
        for (let h = 0; h < heads; h += 1) {
          const srcBase = (((l * regions + r) * batch + b) * heads + h) * frames * headDim;
          const srcStart = srcBase + (frames - keep) * headDim;
          out.set(data.subarray(srcStart, srcStart + keep * headDim), dst);
          dst += keep * headDim;
        }
      }
    }
  }
  return out;
}

function trimFusionCache(
  data: Float32Array<ArrayBufferLike>,
  batch: number,
  heads: number,
  frames: number,
  headDim: number,
  keep: number
): Float32Array<ArrayBuffer> {
  const out = new Float32Array(batch * heads * keep * headDim);
  let dst = 0;
  for (let b = 0; b < batch; b += 1) {
    for (let h = 0; h < heads; h += 1) {
      const srcBase = (b * heads + h) * frames * headDim;
      const srcStart = srcBase + (frames - keep) * headDim;
      out.set(data.subarray(srcStart, srcStart + keep * headDim), dst);
      dst += keep * headDim;
    }
  }
  return out;
}
